import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

export function useMailbox(walletAddress) {
  const [mails, setMails] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all mails for the user
  const fetchMails = useCallback(async () => {
    if (!walletAddress) {
      setMails([]);
      setUnreadCount(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const now = new Date().toISOString();

      const { data, error: fetchError } = await supabase
        .from('mailbox')
        .select('*')
        .ilike('wallet_address', walletAddress)
        .or(`expires_at.is.null,expires_at.gt.${now}`)
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      setMails(data || []);
      setUnreadCount((data || []).filter(mail => !mail.is_read).length);
    } catch (err) {
      console.error('Error fetching mails:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [walletAddress]);

  // Fetch unread count only (lighter query)
  const fetchUnreadCount = useCallback(async () => {
    if (!walletAddress) {
      setUnreadCount(0);
      return;
    }

    try {
      const now = new Date().toISOString();

      const { count, error: countError } = await supabase
        .from('mailbox')
        .select('*', { count: 'exact', head: true })
        .ilike('wallet_address', walletAddress)
        .eq('is_read', false)
        .or(`expires_at.is.null,expires_at.gt.${now}`);

      if (countError) {
        throw countError;
      }

      setUnreadCount(count || 0);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, [walletAddress]);

  // Mark a mail as read
  const markAsRead = useCallback(async (mailId) => {
    try {
      const { error: updateError } = await supabase
        .from('mailbox')
        .update({ is_read: true })
        .eq('id', mailId);

      if (updateError) {
        throw updateError;
      }

      // Update local state
      setMails(prev => prev.map(mail =>
        mail.id === mailId ? { ...mail, is_read: true } : mail
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));

      return { success: true };
    } catch (err) {
      console.error('Error marking mail as read:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Mark all mails as read
  const markAllAsRead = useCallback(async () => {
    if (!walletAddress) return { success: false };

    try {
      const { error: updateError } = await supabase
        .from('mailbox')
        .update({ is_read: true })
        .ilike('wallet_address', walletAddress)
        .eq('is_read', false);

      if (updateError) {
        throw updateError;
      }

      // Update local state
      setMails(prev => prev.map(mail => ({ ...mail, is_read: true })));
      setUnreadCount(0);

      return { success: true };
    } catch (err) {
      console.error('Error marking all as read:', err);
      return { success: false, error: err.message };
    }
  }, [walletAddress]);

  // Claim reward from mail (using Edge Function)
  const claimReward = useCallback(async (mailId) => {
    try {
      const mail = mails.find(m => m.id === mailId);
      if (!mail) {
        throw new Error('Mail not found');
      }
      if (mail.is_claimed) {
        throw new Error('Reward already claimed');
      }
      if (!mail.reward_type || !mail.reward_amount) {
        throw new Error('No reward in this mail');
      }

      // Edge Function 호출
      const { data, error } = await supabase.functions.invoke('claim-mail-reward', {
        body: { mailId },
      });

      if (error) {
        throw new Error(data?.error || error.message || 'Failed to claim reward');
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to claim reward');
      }

      // Update local state
      setMails(prev => prev.map(m =>
        m.id === mailId ? { ...m, is_claimed: true, is_read: true } : m
      ));

      return {
        success: true,
        rewardType: mail.reward_type,
        rewardAmount: mail.reward_amount,
        claimedItems: data.claimedItems || [],
      };
    } catch (err) {
      console.error('Error claiming reward:', err);
      return { success: false, error: err.message };
    }
  }, [mails]);

  // Delete a mail
  const deleteMail = useCallback(async (mailId) => {
    try {
      const { error: deleteError } = await supabase
        .from('mailbox')
        .delete()
        .eq('id', mailId);

      if (deleteError) {
        throw deleteError;
      }

      // Update local state
      const deletedMail = mails.find(m => m.id === mailId);
      setMails(prev => prev.filter(mail => mail.id !== mailId));
      if (deletedMail && !deletedMail.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      return { success: true };
    } catch (err) {
      console.error('Error deleting mail:', err);
      return { success: false, error: err.message };
    }
  }, [mails]);

  // Initial fetch when wallet changes
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  return {
    mails,
    unreadCount,
    loading,
    error,
    fetchMails,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    claimReward,
    deleteMail,
    refresh: fetchMails,
  };
}
